import React, { useState, useCallback } from 'react';
import { processAndVerify, getAllSavedEmails } from '../api/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
    Search, Mail, CheckCircle, XCircle, AlertCircle, HelpCircle,
    Loader2, Building2, Copy,
    ChevronDown, ChevronUp, Clipboard, ShieldCheck, ShieldAlert,
    ShieldX, Zap
} from 'lucide-react';

export default function EmailFinder() {
    const [htmlInput, setHtmlInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    // const [savedContacts, setSavedContacts] = useState([]); // Removed as we use polling for current batch logic differently
    const [currentBatchId, setCurrentBatchId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Polling for updates
    const checkBatchStatus = useCallback(async () => {
        try {
            // Sadece ilgili batchId'yi çekiyoruz, performansı artırır
            const { data } = await getAllSavedEmails({ batchId: currentBatchId });
            if (data.data) {
                const batchResults = data.data; // Server zaten filtreledi

                if (batchResults.length > 0) {
                    setContacts(prevContacts => {
                        const newContacts = [...prevContacts];
                        let updatedCount = 0;

                        batchResults.forEach(saved => {
                            // İsme ve şirkete göre eşleştir (veya varsa index)
                            const idx = newContacts.findIndex(c =>
                                c.name === `${saved.firstName} ${saved.lastName}` &&
                                (c.company === saved.company || c.company === saved.companyNameForEmails) &&
                                !c.isVerified // Zaten güncellenmemişse
                            );

                            if (idx !== -1) {
                                if (saved.emailStatus === 'Pending') return; // Henüz sonuçlanmadı, beklemeye devam et

                                let status = 'unknown';
                                if (saved.emailStatus === 'Verified') status = 'valid';
                                else if (saved.emailStatus === 'Invalid') status = 'invalid';
                                else if (saved.emailStatus === 'Risky') status = 'risky';

                                newContacts[idx] = {
                                    ...newContacts[idx],
                                    emails: [{ email: saved.email, pattern: 'best_match', verification: { status } }],
                                    bestEmail: { email: saved.email, verification: { status } },
                                    isVerified: true,
                                    domainCheck: { valid: true }
                                };
                                updatedCount++;
                            }
                        });

                        // Hepsi tamamlandı mı? (Basit bir kontrol, gerekirse iyileştirilebilir)
                        if (batchResults.length >= newContacts.length || (updatedCount === 0 && batchResults.length > 0)) {
                            // Eger degisiklik yoksa ve sure gectiyse durdurabiliriz ama simdilik devam
                        }

                        // Summary guncelle
                        const validCount = batchResults.filter(r => r.emailStatus === 'Verified').length;
                        setSummary(prev => ({
                            ...prev,
                            valid: validCount,
                            total: newContacts.length
                        }));

                        return newContacts;
                    });
                }
            }
        } catch (err) {
            console.error('Polling error:', err);
        }
    }, [currentBatchId]);

    React.useEffect(() => {
        let interval;
        if (isProcessing && currentBatchId) {
            interval = setInterval(checkBatchStatus, 3000);
        }
        return () => clearInterval(interval);
    }, [isProcessing, currentBatchId, checkBatchStatus]);

    const handleProcess = useCallback(async () => {
        if (!htmlInput.trim()) {
            setError('Lütfen Apollo HTML içeriğini yapıştırın.');
            return;
        }

        setError('');
        setLoading(true);
        setContacts([]);
        setSummary(null);
        setExpandedRows({});
        setIsProcessing(false);
        setCurrentBatchId(null);

        try {
            const { data } = await processAndVerify(htmlInput);

            if (!data.contacts || data.contacts.length === 0) {
                setError('HTML içinden kişi bilgisi bulunamadı.');
                setLoading(false);
                return;
            }

            // İlk parse edilen listeyi göster
            setContacts(data.contacts);
            setSummary({ total: data.contacts.length, valid: 0, risky: 0, invalid: 0 });

            if (data.batchId) {
                setCurrentBatchId(data.batchId);
                setIsProcessing(true);
                toast.success(`İşlem başlatıldı! ${data.contacts.length} kişi aranıyor...`);
            } else {
                // Eski usul senkron cevap (geriye uyumluluk)
                if (data.results) {
                    setContacts(data.results);
                    setSummary(data.summary);
                    toast.success('İşlem tamamlandı!');
                }
            }

        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'İşlem hatası';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [htmlInput]);

    const toggleRow = (index) => {
        setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const copyEmail = (email, e) => {
        if (e) e.stopPropagation();
        navigator.clipboard.writeText(email);
        toast.success('Email kopyalandı!');
    };

    const copyAllValid = () => {
        const validEmails = [];
        contacts.forEach(c => {
            if (c.bestEmail?.verification?.status === 'valid') validEmails.push(c.bestEmail.email);
            // Veya emails arrayinden
            c.emails?.forEach(e => {
                if (e.verification?.status === 'valid') validEmails.push(e.email);
            });
        });
        // Unique
        const unique = [...new Set(validEmails)];

        if (unique.length > 0) {
            navigator.clipboard.writeText(unique.join('\n'));
            toast.success(`${unique.length} geçerli email kopyalandı!`);
        } else {
            toast.info('Geçerli email bulunamadı');
        }
    };

    const getStatusBadge = (status) => {
        const config = {
            valid: { style: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: <ShieldCheck className="h-3.5 w-3.5" />, label: 'Geçerli' },
            invalid: { style: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: <ShieldX className="h-3.5 w-3.5" />, label: 'Geçersiz' },
            risky: { style: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: <ShieldAlert className="h-3.5 w-3.5" />, label: 'Riskli' },
            unknown: { style: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', icon: <HelpCircle className="h-3.5 w-3.5" />, label: 'Bilinmiyor' },
            not_checked: { style: 'bg-gray-50 text-gray-400 dark:bg-gray-900 dark:text-gray-500', icon: null, label: 'Kontrol edilmedi' },
            no_mx: { style: 'bg-orange-50 text-orange-400 dark:bg-orange-900/20 dark:text-orange-500', icon: <XCircle className="h-3.5 w-3.5" />, label: 'MX Yok' },
            processing: { style: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 animate-pulse', icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />, label: 'Aranıyor...' }
        };
        const c = config[status] || config.unknown;
        return (
            <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', c.style)}>
                {c.icon} {c.label}
            </span>
        );
    };

    const getContactValidCount = (contact) => {
        if (contact.bestEmail?.verification?.status === 'valid') return 1;
        return contact.emails?.filter(e => e.verification?.status === 'valid').length || 0;
    };

    return (
        <div className="space-y-6 pt-12 animate-in fade-in duration-300">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg">
                    <Search className="h-5 w-5" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Email Finder</h1>
                    <p className="text-sm text-muted-foreground">Apollo HTML yapıştır → otomatik email bul ve doğrula</p>
                </div>
            </div>

            {/* Input */}
            <Card className="border-t-4 border-t-violet-500 shadow-lg">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Clipboard className="h-5 w-5 text-violet-500" />
                        Apollo HTML Yapıştır
                    </CardTitle>
                    <CardDescription>
                        Apollo.io kişi tablosunu İncele → HTML kopyala → buraya yapıştır. Butona bas, anında listelenir ve arka planda taranır.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="relative">
                        <textarea
                            value={htmlInput}
                            onChange={(e) => setHtmlInput(e.target.value)}
                            placeholder='<div class="zp_XgaPk" role="rowgroup">...'
                            className="w-full h-40 px-4 py-3 rounded-lg border bg-muted/30 font-mono text-xs resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all placeholder:text-muted-foreground/50"
                            disabled={loading || isProcessing}
                        />
                        {htmlInput && (
                            <span className="absolute top-2 right-2 text-xs text-muted-foreground bg-background/80 px-2 py-0.5 rounded">
                                {(htmlInput.length / 1024).toFixed(1)} KB
                            </span>
                        )}
                    </div>

                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Hata</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="flex gap-3">
                        <Button
                            onClick={handleProcess}
                            disabled={!htmlInput.trim() || loading || isProcessing}
                            className="flex-1 h-11 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold shadow-md transition-all hover:scale-[1.01]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Başlatılıyor...
                                </>
                            ) : isProcessing ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Arka planda taranıyor...
                                </>
                            ) : (
                                <>
                                    <Zap className="mr-2 h-4 w-4" />
                                    Otomatik Bul ve Doğrula
                                </>
                            )}
                        </Button>
                        {htmlInput && !loading && !isProcessing && (
                            <Button
                                variant="outline"
                                onClick={() => { setHtmlInput(''); setContacts([]); setError(''); setSummary(null); }}
                                className="h-11"
                            >
                                Temizle
                            </Button>
                        )}
                        <Button
                            variant="secondary"
                            className="h-11"
                            onClick={() => window.open('/saved-emails', '_blank')}
                        >
                            Kaydedilen Verileri Görüntüle
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Summary Bar */}
            {summary && (
                <Card className="border-t-4 border-t-green-500 bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20 shadow-md">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                                {isProcessing ? (
                                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                                ) : (
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                )}
                                <div>
                                    <span className="font-semibold text-sm">
                                        {isProcessing ? 'Tarama Devam Ediyor...' : 'Tarama Tamamlandı'}
                                    </span>
                                    <p className="text-xs text-muted-foreground">{summary.total} kişi listelendi</p>
                                </div>
                            </div>
                            <div className="flex gap-2 text-xs flex-wrap">
                                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 font-medium">
                                    <ShieldCheck className="h-3.5 w-3.5" /> {summary.valid} geçerli
                                </span>
                            </div>
                        </div>

                        {summary.valid > 0 && !isProcessing && (
                            <div className="mt-3">
                                <Button variant="outline" size="sm" onClick={copyAllValid}
                                    className="text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-950/20">
                                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                                    Tüm Geçerli Emailleri Kopyala ({summary.valid})
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Contact Cards */}
            {contacts.length > 0 && (
                <div className="space-y-3">
                    {contacts.map((contact, idx) => {
                        const validCount = getContactValidCount(contact);
                        const isPending = !contact.isVerified && isProcessing;

                        return (
                            <Card key={idx} className={cn(
                                "transition-all duration-200 hover:shadow-md",
                                isPending ? "border-l-4 border-l-blue-300 opacity-80" :
                                    validCount > 0
                                        ? "border-l-4 border-l-green-500"
                                        : "border-l-4 border-l-gray-300"
                            )}>
                                {/* Contact Header */}
                                <div
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                                    onClick={() => toggleRow(idx)}
                                >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white font-bold text-xs">
                                            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : contact.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-sm truncate">{contact.name}</h3>
                                                {isPending ? (
                                                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full animate-pulse">Aranıyor...</span>
                                                ) : (
                                                    validCount > 0 ? (
                                                        <span className="text-xs font-medium text-green-600 dark:text-green-400">
                                                            ✅ Doğrulandı
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-medium text-gray-500">
                                                            Sonuç bulunamadı
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
                                                {contact.title && <span className="truncate max-w-[180px]">{contact.title}</span>}
                                                {contact.company && (
                                                    <span className="flex items-center gap-1">
                                                        <Building2 className="h-3 w-3" />{contact.company}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {expandedRows[idx]
                                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                        }
                                    </div>
                                </div>

                                {/* Expanded Email List */}
                                {expandedRows[idx] && (
                                    <div className="border-t px-4 py-3 bg-muted/10 space-y-1.5 animate-in slide-in-from-top-2 duration-200">
                                        {contact.emails && contact.emails.length > 0 ? (
                                            <div className="grid gap-1">
                                                {contact.emails.map((emailObj, eIdx) => (
                                                    <div
                                                        key={eIdx}
                                                        className={cn(
                                                            "flex items-center justify-between px-3 py-1.5 rounded-lg border transition-all text-sm",
                                                            emailObj.verification?.status === 'valid' && "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                                            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                                            <span className="font-mono text-xs truncate">{emailObj.email}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                                            {emailObj.verification && getStatusBadge(emailObj.verification.status)}
                                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                                                                onClick={(e) => copyEmail(emailObj.email, e)}>
                                                                <Copy className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-muted-foreground italic py-2">
                                                {isPending ? 'Email kombinasyonları deneniyor...' : 'Email bulunamadı.'}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
